// Docs · Playground: the screen, API client & HTTP requests, test events, Save / Build / Make live.
import { C, Code, DocLink, H2, H3, Lead, List, Note, P, Steps, Table } from '../parts'

export function PlaygroundScreen() {
  return (
    <>
      <Lead>The playground is where you write, run and test a lender’s code. It opens on its own page and works like VS Code, on a real Node.js container of your own.</Lead>
      <H2>Opening it</H2>
      <P>Lender → tab 3 → <b>3.3 Playground</b> → <b>Open playground · version</b>, or a version’s <b>Open</b> in 3.2 API batches. One sandbox (container) is made per lender and version; the green dot at the bottom left says it is ready.</P>
      <H2>The top bar</H2>
      <Table head={['Control', 'What it does']} rows={[
        ['← back', 'to 3.2 API batches (asks first when something is not saved)'],
        ['<lender>.batchApi · version', 'which version is open, and its status; pick another'],
        ['saved · abc123 / ● unsaved changes', 'the last commit, or that you have changes not saved'],
        ['UAT / Production', 'which address and keys the sandbox uses (admins). Production shows a red bar'],
        ['Run (Ctrl+Enter)', 'runs the test events of the endpoint open in the editor'],
        ['Run all tests', 'every test event of every endpoint; all green → tests passed'],
        ['Discover endpoints', 'reads src/api and writes .hub/manifest.json: endpoints, outcomes, variables'],
        ['Save (Ctrl+S)', 'one commit in the lender’s git, with the message from Source control (or “Update”)'],
        ['Build', 'admins; after tests passed and saved: the image, tests inside it, version fixed'],
        ['Make live', 'admins; after built: production container first, then the live workflows move'],
        ['+', 'New version from this one (e.g. 1.2.0 → 1.3.0)'],
        ['cap icon', 'this documentation, in a new tab'],
      ]} />
      <H2>The left bar</H2>
      <Table head={['Icon', 'View']} rows={[
        ['Explorer', 'the files: open, new file, new folder, rename, delete. Red = a problem (e.g. a file in src/api without defineApi)'],
        ['Search', 'find text in every file'],
        ['Testing', 'the test events per endpoint: run one, run an endpoint’s, + add one. A red dot = a failed test'],
        ['Source control', 'what changed since the last Save; the commit message'],
        ['API client (paper plane)', 'call your endpoints (My APIs) or any address (HTTP request). See API client'],
        ['green / amber / red dot', 'the sandbox: ready / starting / not running'],
        ['↻', 'Re-seed: the sandbox again from the Hub’s copy (throws away files not saved)'],
        ['⚙', 'Runner settings: memory per container, time per lender call'],
      ]} />
      <H2>The bottom panel</H2>
      <Table head={['Tab', 'Shows']} rows={[
        ['Output', 'Build, Discover and test results, step by step'],
        ['Console', 'your ctx.log lines, every lender call your code made with its reply (keys masked), what the code returned, missing variables'],
        ['Terminal', 'a shell inside the sandbox (npm, node, ls…)'],
      ]} />
      <H2>UAT and Production</H2>
      <P>The switch decides which <C>LENDER_BASE_URL</C> and which tab 5 values the sandbox gets. Work on <b>UAT</b>. Switch to <b>Production</b> only for one final check: the calls are real, with the production keys, and are written to History.</P>
      <H2>Keyboard</H2>
      <Table head={['Keys', 'Does']} rows={[['Ctrl+S', 'Save'], ['Ctrl+Enter', 'Run the open endpoint’s test events'], ['Ctrl+F', 'find in the open file'], ['Ctrl+Space', 'suggestions in the editor']]} />
    </>
  )
}

export function ApiClient() {
  return (
    <>
      <Lead>The paper-plane icon. Two tools in one list: <b>My APIs</b> calls your own endpoints with an applicant, <b>HTTP request</b> calls any address, like Postman.</Lead>
      <H2>My APIs</H2>
      <P>Pick an endpoint, keep or change the input (the test applicant), <b>Send</b>. The answer shows three things: the call your code made to the lender, the lender’s raw reply, and the outcome your code returned.</P>
      <Table head={['Target', 'Runs']} rows={[
        ['Sandbox: my code', 'the code as it is in the editor, even not saved; a real call to the lender'],
        ['Built container', 'a built version exactly as production runs it, through the Hub’s address and key'],
      ]} />
      <P><b>Save</b> keeps the request (shared with the team, per lender). <b>Copy as curl</b> gives the same call for a terminal. <b>Make a test event</b> turns the real reply into a test.</P>
      <H2>HTTP request</H2>
      <P>Check the lender’s API by hand before writing code: any method and address, Params, Authorization (Bearer, Basic, API key), Headers, Body (JSON, text, XML, form-urlencoded, form-data with files, binary), time limit and redirects. <b>Paste a curl</b> from the lender’s document to fill it in.</P>
      <H3>Variables</H3>
      <P><C>{'{{LENDER_API_KEY}}'}</C> anywhere in the address, headers or body is filled in <b>inside the sandbox</b> from tab 5; a secret never reaches the browser and is masked back to its name in the answer. Also: <C>{'{{$guid}}'}</C>, <C>{'{{$timestamp}}'}</C>, <C>{'{{$isoTimestamp}}'}</C>, <C>{'{{$randomInt}}'}</C>.</P>
      <H3>Collections and history</H3>
      <P><b>Save</b> into folders of the lender’s collection (shared), <b>Import</b> a Postman collection, and the page’s history of what you sent.</P>
      <H3>What is our IP?</H3>
      <P>Asks from inside the sandbox which address the internet sees: the one the lender must whitelist. Requests leave from the sandbox (on the server: the server’s IP), never from your browser.</P>
      <Note kind="warning">Our own private addresses (databases, other services, the cloud’s metadata) are refused, also after a redirect. Only internet addresses can be called.</Note>
    </>
  )
}

const EVENT = `{
  "name": "approved with two offers",
  "reply": {
    "status": 200,
    "body": { "status": "APPROVED", "offers": [
      { "loan_amount": 200000, "tenure": 24, "roi": 18.5, "emi": 10050 },
      { "loan_amount": 150000, "tenure": 12, "roi": 17, "emi": 13680 }
    ] }
  },
  "expected": "offer",
  "source": "captured"
}`

export function TestEvents() {
  return (
    <>
      <Lead>A test event is a <b>saved lender reply</b> plus the <b>outcome</b> your code must give for it. Tests make sure your code reads every reply right, today and after every change.</Lead>
      <H2>How a test runs</H2>
      <List ordered>
        <li>The Hub runs your handler with the test applicant and <C>ctx.prev</C> = <C>{'{ outcome: \'ok\', reply_code: \'LEAD_CREATED\', lender_ref: \'DEMO-9001\' }'}</C>.</li>
        <li><C>ctx.lender()</C> does <b>not</b> call the lender: it returns the saved reply (every call in that run gets the same reply).</li>
        <li>The outcome must equal the expected one, and pass the <DocLink to="outcome#the-system-check">system check</DocLink>. Green, or red with the reason.</li>
      </List>
      <H2>Which test events to make</H2>
      <List>
        <li><b>One per outcome</b> the endpoint can return (ok, offer, rejected…).</li>
        <li>One for a <b>lender error</b> (status 500) and one for <b>no reply</b> (status 0) → <C>error</C>.</li>
        <li>One per lender code you handle in a special way (e.g. <C>422 NOT_ELIGIBLE</C> → rejected).</li>
      </List>
      <H2>Three ways to add one</H2>
      <Table head={['From', 'How']} rows={[
        ['a real reply', 'API client → Send → Make a test event (best: the real shape)'],
        ['by hand', 'Testing → + → paste the reply from the lender’s document'],
        ['a captured code', 'a code the Hub saw in production and never knew → Alert → Add as a test'],
      ]} />
      <H2>Where they are kept</H2>
      <P>In the Hub, and in git as one file per endpoint: <C>tests/v1.get-offer.replies.json</C> for <C>/v1/get-offer</C>.</P>
      <Code lang="json" title="tests/v1.get-offer.replies.json (one item)">{EVENT}</Code>
      <Note kind="tip">Logging in inside the code? In a test, the login call also gets the saved reply. Skip the login when <C>ctx.mode === 'sample'</C>: see <DocLink to="recipes#login-token">Recipes → Login token</DocLink>.</Note>
      <H2>Build runs them again</H2>
      <P><b>Build</b> needs every test green, then runs them all again <b>inside the built image</b>. A version that is built has passed all its tests twice.</P>
    </>
  )
}

export function Release() {
  return (
    <>
      <Lead>From saved code to production, and back if something is wrong.</Lead>
      <H2>The steps</H2>
      <Steps items={[
        ['Save', <>Each Save is one commit in the lender’s git (branch = version, e.g. <C>1.2.0</C>).</>],
        ['Run all tests', <>All green → the version is <b>tests passed</b>.</>],
        ['Build (admins)', <>Installs the packages (<C>npm ci</C>), runs Discover and every test inside a new image, stores it in our registry with its checksum, tags git <C>v1.2.0</C>. The version is now <b>built</b> and read-only.</>],
        ['Workflow and endpoint', <>3.4: a workflow on this batch, <b>Run all paths</b> green. 3.5: the product’s service endpoint on that workflow version.</>],
        ['Make live (admins)', <>The production container starts first; then the live workflows switch to this version after the C1 check. The old live version becomes <b>previous</b> and keeps running.</>],
        ['Switch the lender on', <>The Active switch on the Lenders list: the web app sees the lender within the cache time.</>],
      ]} />
      <H2>Changing live code</H2>
      <P>A built or live version never changes. Press <b>+ New version</b>: it copies the files (e.g. 1.2.0 → 1.3.0), you change and test, then Build and Make live again.</P>
      <H2>Roll back</H2>
      <P>3.2 API batches → the <b>previous</b> version → <b>Roll back</b>. One step, because its container is still running. A workflow: 3.5 → Switch version.</P>
      <H2>Stop a lender at once</H2>
      <P>The lender’s <b>Active switch Off</b> (with a reason): every endpoint of the lender is suspended immediately.</P>
      <H3>Version numbers</H3>
      <Table head={['Change', 'Example']} rows={[['a fix or a new lender code', '1.2.0 → 1.3.0 (New version does this)'], ['a big change of the lender’s API', '1.x → 2.0.0 (type it in New version)']]} />
    </>
  )
}
